/**
 * PLATFORM-DESIGN-SYSTEM-CODIFICATION-V1 -- THE LEXICON (the ONE
 * plain-words copy home).
 *
 * CANONICAL copy is MECHANICAL EXTRACTION from the binding sources -- no
 * new copy invented here:
 *  - the Money before/after copy table,
 *    docs/design-studio/money-workspace/JUDGING-PACK.md s.6 (BINDING per
 *    CONFORMANCE-CONTRACT.md s.4 COPY LAW), including its kept-plain
 *    list;
 *  - the enterprise copy law as carried by
 *    owner-home/CONFORMANCE-CONTRACT.md C-3/C-4 (fixed vocabulary, no
 *    free adjectives, no manufactured urgency);
 *  - the DESIGN-ONLY tag grammar
 *    (lead-360/APPROVED-DIRECTION-SYSTEM-EXTRACT.md, NEW PATTERNS item
 *    6).
 *
 * ENFORCEMENT: tests/unit/design-system-conformance-guard.test.ts scans
 * dashboard code for the banned lists below (comment-stripped; explicit
 * shrinking allowlist; new files never exempt). The banned lists are
 * exported FROM HERE so the guard and the docs index point at one home.
 *
 * CONTROLLED-VOCABULARY EXEMPTION (stated in the copy table itself):
 * engine status chips keep their controlled vocabulary (DAY_STATUS_LABELS
 * "Open / Count due / Closed"; the StatusChip canon; SLA severity
 * labels). A controlled engine token is never a lexicon violation --
 * changing one is an engine decision, not a render decision.
 */

/** The Money plain-words register (JUDGING-PACK.md s.6 AFTER column). */
export const MONEY_LEXICON = Object.freeze({
  /** Hero label, live day (table row 1). */
  cashHero: 'CASH IN THE DRAWER',
  /** Hero label, closed past day (row 2). */
  cashHeroClosedDay: 'IN THE DRAWER AT CLOSE',
  /** Secondary figure label, live day (row 3). */
  collectedToday: 'COLLECTED TODAY',
  /** The one plain non-cash note (row 4). */
  nonCashNote: 'UPI and card money never comes to the drawer',
  /** The proof expand-in-place row label (rows 5-8). */
  proofRowLabel: 'Where this number comes from',
  /** Duty area title (row 9). */
  dutyAreaTitle: 'WHAT TO DO NOW',
  /** Duty fix actions (rows 10-13; CONFORMANCE-CONTRACT.md s.3). */
  fixCountTheDrawer: 'Count the drawer',
  fixAddPhoto: 'Add photo',
  fixOpenThatDay: 'Open that day',
  fixTakeCashToBank: 'Take cash to the bank',
  /** Close CTA (row 14). */
  closeDay: 'Close day',
  /** Empty duty queue (row 16). */
  nothingToDo: 'Nothing to do right now',
  /** Closed duty queue (row 17). */
  closedCountMatched: 'Closed - the count matched',
  /** Kept-plain book vocabulary (the table's kept list). */
  carriedForwardNeverTyped: 'Carried forward - never typed',
  carriedNeverTyped: 'carried, never typed',
  inTheDrawer: 'in the drawer',
  neverInTheDrawer: 'never in the drawer',
} as const);

/**
 * The DESIGN-ONLY tag grammar (lead F1 NEW PATTERNS item 6; the amber
 * drawn-but-unbuilt chip; untagged pixels are engine-real by definition).
 */
export const DESIGN_ONLY_TAG_LABEL =
  'DESIGN-ONLY - REQUIRES FUNCTIONAL APPROVAL';

/** S2 all-clear default (the extract's explicit all-clear row; callers
 *  with a surface-contract phrase pass their own, e.g. the Money
 *  nothingToDo above). */
export const NEEDS_YOU_ALL_CLEAR = 'Nothing needs you right now.';

/* ----------------------------- BANNED ------------------------------- */

/**
 * Engine task codes that must NEVER render as user-facing pills/text
 * (money CONFORMANCE-CONTRACT.md s.2 NEEDS-YOU: "Task-code pills are
 * forbidden"; each maps to a Problem -> Fix row in s.3). Lawful as DATA
 * (task.kind comparisons); banned in JSX text position.
 */
export const BANNED_MACHINE_TOKENS_IN_UI = Object.freeze([
  'close_day',
  'missing_proof',
  'carried_short',
  'deposit_due',
] as const);

/**
 * Accountant register banned on human (default) screens -- the money
 * verdict's plain-words pass (accountant vocabulary lives inside
 * expansions; JUDGING-PACK.md s.6 BEFORE column, s.7 "accountant terms
 * above the book -> 0"). ONLY terms the table retires EVERYWHERE are
 * listed (statically enforceable); placement-conditional register (e.g.
 * "not derived yet", lawful inside the proof row) is each surface's
 * conformance check, not a static ban.
 */
export const BANNED_ACCOUNTANT_TERMS = Object.freeze([
  'count diff',
] as const);

/**
 * Manufactured-urgency adjectives, banned in authored UI copy
 * (owner-home/CONFORMANCE-CONTRACT.md C-4 "Banned everywhere: urgency
 * adjectives"). 'critical' is additionally banned in ACTION copy but
 * stays lawful inside controlled severity/clinical vocabularies (SLA
 * severity labels; clinical alerts) -- the guard allowlists those
 * controlled homes explicitly.
 */
export const BANNED_URGENCY_ADJECTIVES = Object.freeze([
  'urgent',
  'asap',
  'critical',
] as const);

/**
 * Verbs banned as the LEAD VERB of a PRIMARY action (the S3 signature,
 * owner-home/SYSTEM-EXTRACT.md: never Review / View / See more as
 * primary; primaries are verb + object with the real count or named
 * object). Owner Home's own C-4 additionally bans bare "Open" as primary
 * ON THAT SURFACE (surface law, not repeated here -- "Open patient
 * record" is a lawful verb+object primary on the converted Lead frame).
 */
export const BANNED_PRIMARY_LEAD_VERBS = Object.freeze([
  'review',
  'view',
  'see more',
] as const);

/**
 * THE PRIMARY-COPY CHECK (pure; pinned in tests; consumed by surface
 * suites): returns the violated rule for a primary-action label, or null
 * when the label is lawful.
 */
export function primaryActionCopyViolation(label: string): string | null {
  const normalized = label.trim().toLowerCase();
  if (normalized.length === 0) return 'empty primary label';
  for (const verb of BANNED_PRIMARY_LEAD_VERBS) {
    if (normalized === verb || normalized.startsWith(`${verb} `)) {
      return `banned primary lead verb: "${verb}" (S3: verb + object, never a passive read verb)`;
    }
  }
  for (const adjective of BANNED_URGENCY_ADJECTIVES) {
    if (new RegExp(`\\b${adjective}\\b`, 'i').test(normalized)) {
      return `manufactured urgency adjective: "${adjective}" (C-4: banned in action copy)`;
    }
  }
  return null;
}
